import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Users, CreditCard, Phone, FileText, BarChart3, Package, Ticket, Megaphone, Tag, UserPlus, DollarSign, Clock, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from "../../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-[#132218] border-emerald-900/30 hover:border-emerald-700/40 transition-colors" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [payoutSchedule, setPayoutSchedule] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`, { headers }).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/connect/payout-schedule`, { headers }).then(r => setPayoutSchedule(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-gray-500 text-center py-20">Loading...</div>;

  const cards = [
    { icon: Users, label: 'Total Users', value: stats.total_users, color: 'bg-emerald-600' },
    { icon: Users, label: 'Active Users', value: stats.active_users, color: 'bg-green-600' },
    { icon: CreditCard, label: 'Subscriptions', value: stats.active_subscriptions, color: 'bg-teal-600' },
    { icon: Phone, label: 'Phone Numbers', value: stats.total_phone_numbers, color: 'bg-cyan-600' },
    { icon: BarChart3, label: 'Total Calls', value: stats.total_calls, color: 'bg-blue-600' },
    { icon: FileText, label: 'Invoices', value: stats.total_invoices, color: 'bg-indigo-600' },
    { icon: Users, label: 'Contacts', value: stats.total_contacts, color: 'bg-purple-600' },
    { icon: Ticket, label: 'Tickets', value: stats.total_tickets, color: 'bg-amber-600' },
    { icon: Megaphone, label: 'Campaigns', value: stats.total_campaigns, color: 'bg-orange-600' },
    { icon: Package, label: 'Products', value: stats.total_products, color: 'bg-rose-600' },
    { icon: Tag, label: 'Promo Codes', value: stats.total_promo_codes, color: 'bg-pink-600' },
    { icon: UserPlus, label: 'Affiliates', value: stats.total_affiliates, color: 'bg-violet-600' },
  ];

  return (
    <div data-testid="admin-dashboard-page">
      {/* Green accent bar */}
      <div className="h-1 bg-gradient-to-r from-emerald-600 via-green-500 to-teal-500 rounded-full mb-6" />

      <h1 className="text-2xl font-bold text-white mb-1">Dashboard</h1>
      <p className="text-gray-500 text-sm mb-6">System overview and key metrics</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {cards.map(c => <StatCard key={c.label} {...c} />)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#132218] border-emerald-900/30">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-400" />Revenue</CardTitle></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-emerald-400">${stats.mrr?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-gray-500 mt-1">Monthly Recurring Revenue</p>
          </CardContent>
        </Card>
        <Card className="bg-[#132218] border-emerald-900/30">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" />Recent Activity</CardTitle></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-emerald-400">{stats.recent_signups_7d}</p>
            <p className="text-xs text-gray-500 mt-1">New signups in last 7 days</p>
          </CardContent>
        </Card>

        {/* Payout Schedule Card */}
        <Card className="bg-[#132218] border-emerald-900/30" data-testid="payout-schedule-card">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-400" />Affiliate Payouts</CardTitle></CardHeader>
          <CardContent>
            {payoutSchedule ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Schedule</span>
                  <span className="text-emerald-400 font-medium capitalize">{payoutSchedule.schedule}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Next Payout</span>
                  <span className="text-white font-medium">{payoutSchedule.next_payout_date ? new Date(payoutSchedule.next_payout_date).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Pending</span>
                  <span className="text-amber-400 font-bold">${payoutSchedule.pending_total?.toFixed(2)} ({payoutSchedule.pending_affiliates} affiliates)</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Loading...</p>
            )}
          </CardContent>
        </Card>
      </div>

      {Object.keys(stats.plan_distribution || {}).length > 0 && (
        <Card className="bg-[#132218] border-emerald-900/30 mt-4">
          <CardHeader><CardTitle className="text-white text-base">Plan Distribution</CardTitle></CardHeader>
          <CardContent className="flex gap-4 flex-wrap">
            {Object.entries(stats.plan_distribution).map(([plan, count]) => (
              <div key={plan} className="px-4 py-2 rounded-lg bg-emerald-600/10 border border-emerald-600/20">
                <p className="text-white font-medium capitalize">{plan.replace('_', ' ')}</p>
                <p className="text-emerald-400 text-lg font-bold">{count}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;
